import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { BundlePreview } from '@/lib/api/generated/model';

import { namedChoice, workflowTree } from '../bundles/includes';
import { useBundlePreview } from '../hooks/useBundlePreview';
import { useSharePreview, useShareToLibrary } from '../hooks/useShareToLibrary';
import { useWorkflows } from '../hooks/useWorkflows';
import { FlowDialog, FlowDialogPending } from './FlowDialog';
import {
  keepTicked,
  pieceId,
  rowAction,
  libraryRefs,
  type PieceKind,
} from '../bundles/libraryChoice';
import {
  IncludesChoice,
  IncludesLine,
  WorkflowTree,
  type RowAction,
  type RowOutcome,
} from './IncludesChoice';

interface Picked {
  source: BundlePreview;
  agents: Set<string>;
  skills: Set<string>;
  /**
   * Named pieces the person chose to leave out, as `pieceId`s, with the
   * refusal that led there (dialogs review 2): the row keeps its place, says
   * so, and can be sent again.
   */
  leftOut: Map<string, { canUseLibrary: boolean }>;
  /** Pieces left to the Library's version, as `pieceId`s (spec 212). */
  deferred: Set<string>;
  /** The choice just made, until its check answers: undone if that check fails; whether it ticked. */
  lastDeferred: { id: string; ticked: boolean } | null;
  /** The service's message for a refused choice, kept once the choice is dropped. */
  notice: string | null;
  /** What the status line says once the check after an action answers. */
  announce: string | null;
}

/**
 * Put a workflow in the Library for every workspace (spec 206; service spec 164).
 *
 * The same parts an export carries — the workflow, every workflow it links to,
 * and the agents and skills the person ticks — each becoming its own Library
 * item, marked with who shared it. Before Share can be pressed the dialog says,
 * piece by piece, what will happen: new, an update to what they shared before,
 * already there, or refused because the key means something else in the Library.
 * Shared items appear at once; there is no review step (the 2026-10-19 decision),
 * and the person or an org admin can take them down from the Library.
 *
 * One list (spec 211): each piece once, with what will happen to it, under an
 * Includes line that sums it up. The pieces a prompt names are listed, not
 * ticked (dialogs review 2): one the Library refuses holds the share until the
 * person answers it, by leaving it to the Library's version or leaving it out.
 * Leaving it out is said on its row, because the shared workflow will then name
 * a piece the Library does not hold.
 */
export function ShareToLibraryDialog({
  workspaceId,
  workflow,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  workflow: { id: string; key: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const bundle = useBundlePreview(workspaceId, workflow.id, open);
  const { workflows: views } = useWorkflows(workspaceId);
  const [picked, setPicked] = useState<Picked | null>(null);
  // Every opening starts afresh (spec 212 FR-006): the preview may come back as
  // the same object, which would otherwise keep the last opening's choice.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPicked(null);
  }

  const source = bundle.preview;
  const choice: Picked | null =
    source && picked?.source === source
      ? picked
      : source
        ? {
            source,
            ...namedChoice(source),
            leftOut: new Map(),
            deferred: new Set<string>(),
            lastDeferred: null,
            notice: null,
            announce: null,
          }
        : null;
  const ticks = choice
    ? {
        agents: [...choice.agents],
        skills: [...choice.skills],
        useLibrary: libraryRefs(choice.deferred),
      }
    : null;
  const share = useSharePreview(workspaceId, workflow.id, open ? ticks : null);
  const sharing = useShareToLibrary(workspaceId, workflow.id);

  // The check for the choice just made answered: the choice stands, and a
  // later failure is not about it.
  if (
    choice?.lastDeferred &&
    share.preview &&
    !share.isFetching &&
    !share.isPlaceholderData &&
    !share.error
  ) {
    setPicked({ ...choice, lastDeferred: null });
  }

  // A choice whose check fails is dropped, its message kept: the service cannot
  // honour it (spec 212 FR-007), or the check itself failed, and a row must not
  // read "Uses the Library's" when nothing said so. Dropping it goes back to the
  // last choice that was checked; pressing the action again asks again.
  if (choice?.lastDeferred && share.error && !share.isFetching) {
    const { id, ticked } = choice.lastDeferred;
    const deferred = new Set(choice.deferred);
    deferred.delete(id);
    const { kind, key } = libraryRefs(new Set([id]))[0] ?? {};
    const untick = (chosen: Set<string>, of: string) => {
      const next = new Set(chosen);
      if (ticked && kind === of && key) next.delete(key);
      return next;
    };
    setPicked({
      ...choice,
      agents: untick(choice.agents, 'agent'),
      skills: untick(choice.skills, 'skill'),
      deferred,
      lastDeferred: null,
      notice: share.error ?? null,
      announce: null,
    });
  }

  const pieces = share.preview?.pieces ?? [];
  const blockers = share.preview?.blockers ?? source?.blockers ?? [];
  const ready =
    share.preview?.shareable === true &&
    !share.isFetching &&
    !sharing.isPending;
  const busy = share.isFetching || sharing.isPending;

  /** Leave a piece to the Library: an agent or skill is ticked as well. */
  const leaveToLibrary = (kind: PieceKind, key: string) => {
    if (!choice) return;
    const id = pieceId(kind, key);
    const chosen =
      kind === 'agent'
        ? choice.agents
        : kind === 'skill'
          ? choice.skills
          : null;
    const ticked = chosen !== null && !chosen.has(key);
    const tick = (of: Set<string>, which: PieceKind) =>
      which === kind ? new Set([...of, key]) : of;
    const leftOut = new Map(choice.leftOut);
    leftOut.delete(id);
    setPicked({
      ...choice,
      agents: tick(choice.agents, 'agent'),
      skills: tick(choice.skills, 'skill'),
      leftOut,
      deferred: new Set([...choice.deferred, id]),
      lastDeferred: { id, ticked },
      notice: null,
      announce: `${key} now uses the Library's version.`,
    });
  };
  const sendOurs = (kind: PieceKind, key: string) => {
    if (!choice) return;
    const deferred = new Set(choice.deferred);
    deferred.delete(pieceId(kind, key));
    setPicked({
      ...choice,
      deferred,
      lastDeferred: null,
      notice: null,
      announce: `${key}: your version is checked again.`,
    });
  };
  /** Leave a named piece out: the share goes, and its workflow names a piece the Library lacks. */
  const leaveOut = (
    kind: 'agent' | 'skill',
    key: string,
    canUseLibrary: boolean,
  ) => {
    if (!choice) return;
    const id = pieceId(kind, key);
    const drop = (of: Set<string>, which: PieceKind) => {
      const next = new Set(of);
      if (which === kind) next.delete(key);
      return next;
    };
    const deferred = new Set(choice.deferred);
    deferred.delete(id);
    setPicked({
      ...choice,
      agents: drop(choice.agents, 'agent'),
      skills: drop(choice.skills, 'skill'),
      leftOut: new Map(choice.leftOut).set(id, { canUseLibrary }),
      deferred,
      lastDeferred: null,
      notice: null,
      announce: `${key} is left out.`,
    });
  };
  const sendAgain = (kind: 'agent' | 'skill', key: string) => {
    if (!choice) return;
    const leftOut = new Map(choice.leftOut);
    leftOut.delete(pieceId(kind, key));
    const add = (of: Set<string>, which: PieceKind) =>
      which === kind ? new Set([...of, key]) : of;
    setPicked({
      ...choice,
      agents: add(choice.agents, 'agent'),
      skills: add(choice.skills, 'skill'),
      leftOut,
      notice: null,
      announce: `${key} is sent again.`,
    });
  };
  const named = (kind: PieceKind, key: string) =>
    kind === 'agent'
      ? (source?.agents.some((a) => a.key === key && a.suggested) ?? false)
      : kind === 'skill'
        ? (source?.skills.some((a) => a.key === key && a.suggested) ?? false)
        : false;

  /** A row's actions, in order: the Library's version first, then leaving it out. */
  const actionsFor = (
    kind: PieceKind,
    key: string,
    row: { outcome: RowOutcome['outcome']; canUseLibrary: boolean },
  ): RowAction[] => {
    const actions: RowAction[] = [];
    switch (rowAction(row)) {
      case 'use_library':
        actions.push({
          label: "Use the Library's",
          name: `Use the Library's ${key}`,
          onPress: () => leaveToLibrary(kind, key),
          disabled: busy,
        });
        break;
      case 'send_ours':
        actions.push({
          label: 'Send ours',
          name: `Send ours: ${key}`,
          onPress: () => sendOurs(kind, key),
          disabled: busy,
        });
        break;
      default:
        break;
    }
    if (row.outcome === 'refused' && kind !== 'workflow' && named(kind, key)) {
      actions.push({
        label: 'Leave it out',
        name: `Leave out ${key}`,
        onPress: () => leaveOut(kind, key, row.canUseLibrary),
        disabled: busy,
      });
    }
    return actions;
  };

  const pieceOutcome = (kind: PieceKind, key: string): RowOutcome | null => {
    const piece = pieces.find((p) => p.kind === kind && p.key === key);
    if (!piece) return null;
    const actions = actionsFor(kind, key, {
      outcome: piece.outcome,
      canUseLibrary: piece.can_use_library === true,
    });
    // Only what differs is marked (dialogs review 2): a badge on every row read
    // "Added" nine times, and hid the one row that needed reading. The rest is
    // said once, under the Includes line.
    if (piece.outcome === 'added' && actions.length === 0) return null;
    return { outcome: piece.outcome, reason: piece.reason, actions };
  };
  const addedCount = pieces.filter((p) => p.outcome === 'added').length;
  const restLine =
    addedCount === 0
      ? ''
      : addedCount === pieces.length
        ? addedCount === 1
          ? 'It will be added.'
          : `All ${String(addedCount)} will be added.`
        : `The other ${String(addedCount)} will be added.`;
  const itemOutcome = (
    kind: 'agent' | 'skill',
    key: string,
  ): RowOutcome | null => {
    if (!choice) return null;
    const sent = (kind === 'agent' ? choice.agents : choice.skills).has(key);
    if (!sent) {
      // Only a named piece has a row while not sent: one the person left out.
      const left = choice.leftOut.get(pieceId(kind, key));
      return {
        outcome: 'not_sent',
        reason:
          'Left out. The shared workflow names it, and the Library will not hold it.',
        actions: [
          ...(left?.canUseLibrary
            ? actionsFor(kind, key, {
                outcome: 'not_sent',
                canUseLibrary: true,
              })
            : []),
          {
            label: 'Send it',
            name: `Send ${key}`,
            onPress: () => sendAgain(kind, key),
            disabled: busy,
          },
        ],
      };
    }
    return pieceOutcome(kind, key);
  };

  const close = (next: boolean) => {
    if (sharing.isPending) return;
    onOpenChange(next);
    if (!next) sharing.reset();
  };

  return (
    <FlowDialog
      open={open}
      onOpenChange={close}
      busy={sharing.isPending}
      title={`Share ${workflow.name} to the Library`}
      description="Puts it in the Library for every workspace to install, marked as shared by you; you can take it down at any time."
      alert={choice?.notice ?? share.error ?? sharing.error?.message}
      primary={
        <Button
          disabled={!ready || !ticks}
          onClick={() => {
            if (!ticks) return;
            sharing.mutate(ticks, {
              onSuccess: () => {
                toast.success(`${workflow.name} is in the Library.`);
                close(false);
              },
              // The Library may have changed since the check: ask again.
              onError: () => {
                void share.refetch();
              },
            });
          }}
        >
          {sharing.isPending ? 'Sharing…' : 'Share'}
        </Button>
      }
    >
      {!source || !choice ? (
        bundle.loadFailed ? (
          <p role="alert" className="text-destructive text-sm">
            It could not be checked. Close and try again.
          </p>
        ) : (
          <FlowDialogPending label="Checking…" />
        )
      ) : (
        <>
          <div className="flex flex-col gap-0.5">
            <IncludesLine
              preview={source}
              choice={choice}
              usesLibrary={choice.deferred.size}
            />
            {/* Its place is kept while the first check runs, so nothing below
                moves when it arrives. */}
            <p className="text-muted-foreground min-h-5 text-sm">{restLine}</p>
          </div>
          <WorkflowTree
            rows={workflowTree(source.workflows, views)}
            outcomeOf={(key) => pieceOutcome('workflow', key)}
          />
          <IncludesChoice
            preview={source}
            choice={choice}
            onChange={(next) =>
              setPicked({
                ...choice,
                ...next,
                deferred: keepTicked(choice.deferred, next.agents, next.skills),
                announce: null,
              })
            }
            outcomeOf={itemOutcome}
          />
          {/* Always there, so a re-check neither shifts the rows below nor
              arrives in a region the screen reader has not met yet. */}
          <p role="status" className="text-muted-foreground min-h-4 text-xs">
            {share.isFetching
              ? 'Checking the Library…'
              : (choice.announce ?? '')}
          </p>
          {blockers.length > 0 && (
            <ul
              role="alert"
              className="text-destructive flex flex-col gap-0.5 text-sm"
            >
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </FlowDialog>
  );
}
